import { useState, useEffect, useMemo, startTransition } from 'react';
import { isDemoBrand } from '../utils/constants';
import { formatDateForAPI } from '../utils/formatters';
import { fetchWithBrand } from '../utils/api';

/**
 * Fetches unified Google Ads + GA4 daily performance based on dateRange.
 * Returns raw data and a computed summary.
 */
export function useGoogleAds(brandId, dateRange, startDate, endDate) {
  const [unifiedData, setUnifiedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Guard: skip if custom range is incomplete
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    if (!brandId) return;

    const fetchData = async () => {
      // Demo data
      if (isDemoBrand(brandId)) {
        setIsLoading(true);
        const demo = [
          { date: '2025-09-01', clicks: 1200, ga4_sessions: 3000, ga4_total_revenue: 150000, conversion_value: 120000, cost: 40000, campaign_id: 'G-1' },
          { date: '2025-10-01', clicks: 1400, ga4_sessions: 3400, ga4_total_revenue: 170000, conversion_value: 140000, cost: 45000, campaign_id: 'G-2' },
        ];
        setUnifiedData(demo);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        let url;
        const effectiveStart = dateRange === 'custom' ? startDate : null;
        const effectiveEnd = dateRange === 'custom' ? endDate : null;
        if (effectiveStart && effectiveEnd) {
          const startDateStr = formatDateForAPI(effectiveStart);
          const endDateStr = formatDateForAPI(effectiveEnd);
          url = `/api/google-ads/unified-googleads-ga4-performance?dateRange=custom&startDate=${startDateStr}&endDate=${endDateStr}`;
        } else {
          url = `/api/google-ads/unified-googleads-ga4-performance?dateRange=${dateRange}`;
        }

        const response = await fetchWithBrand(url, brandId);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        startTransition(() => {
          setUnifiedData(data);
        });
      } catch {
        setUnifiedData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [brandId, dateRange, startDate, endDate]);

  // Compute summary from unified data
  const googleAdsSummary = useMemo(() => {
    if (!unifiedData || unifiedData.length === 0) {
      return {
        totalClicks: 0,
        totalSessions: 0,
        totalConversions: 0,
        totalGa4Purchases: 0,
        totalGa4Revenue: 0,
        totalGoogleAdsRevenue: 0,
        totalCost: 0,
        campaignCount: 0,
        cpc: 0,
        cps: 0,
        roas: 0,
      };
    }

    const totals = unifiedData.reduce((acc, item) => {
      acc.totalClicks += item.clicks || 0;
      acc.totalSessions += item.ga4_sessions || 0;
      acc.totalConversions += item.conversions || 0;
      acc.totalGa4Purchases += item.ga4_purchases || 0;
      acc.totalGa4Revenue += item.ga4_total_revenue || 0;
      acc.totalGoogleAdsRevenue += item.conversion_value || 0;
      acc.totalCost += item.cost || 0;
      return acc;
    }, {
      totalClicks: 0,
      totalSessions: 0,
      totalConversions: 0,
      totalGa4Purchases: 0,
      totalGa4Revenue: 0,
      totalGoogleAdsRevenue: 0,
      totalCost: 0,
    });

    const campaignIds = new Set(unifiedData.map(item => item.campaign_id));
    const cpc = totals.totalClicks > 0 ? totals.totalCost / totals.totalClicks : 0;
    const cps = totals.totalSessions > 0 ? totals.totalCost / totals.totalSessions : 0;
    const roas = totals.totalCost > 0 ? totals.totalGoogleAdsRevenue / totals.totalCost : 0;

    return {
      ...totals,
      campaignCount: campaignIds.size,
      cpc,
      cps,
      roas,
    };
  }, [unifiedData]);

  return {
    unifiedData,
    googleAdsSummary,
    isLoading,
  };
}
